import React from "react";
import useBackend, { useLogin } from "../../hooks/use_backend";
import { Grid, Container } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import {
  fetchInstallerCommissioning,
  fetchInstallerErrors,
  fetchInstallerInfoStatistics,
} from "../../scripts/api_v2/landing_page_client";

import { useNavigate } from "react-router-dom";
import HealthCard from "./components/health_card";
import InfoStat from "../../components/general/info_stat";
import StatisticsCard from "./components/statistics_card";
import UserActivityCard from "./components/user_activity_card";
import SiteActivityCard from "./components/site_activity_card";
import { formatVolume } from "../../scripts/utils";
import FavouriteSitesCard from "./components/favourites_sites_card";
import CommissioningCard from "./components/commissioning_card";
import { TOOLTIPS_EN } from "../../enums/tooltip_enum_EN";
import { NAVIGATION_PATH } from "../../enums/navigation_enum";
import { useAmplitudeTrack } from "../../scripts/amplitude_utils";

export default function LandingPage() {
  const backend = useBackend();
  const login = useLogin();
  const navigate = useNavigate();
  const { trackEvent, trackButtonClicked } = useAmplitudeTrack();
  const [includeMutes, setIncludeMutes] = React.useState(false);
  const pageLoadStartTime = React.useRef(performance.now());

  React.useEffect(() => {
    trackEvent('home_page_viewed');
  }, [trackEvent]);

  let { data: ErrorData, isLoading: fetchingErrorData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.LANDING_PAGE_ERRORS, backend.fetch, login.company_name, includeMutes],
    queryFn: () => fetchInstallerErrors(backend.fetch, includeMutes),
    refetchInterval: 60000,
  });

  let { data: commissioningData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.LANDING_PAGE_COMMISSIONING, backend.fetch, login.company_name],
    queryFn: () => fetchInstallerCommissioning(backend.fetch),
    refetchInterval: 60000,
  });

  let { data: infoStatisticsData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.LANDING_PAGE_INFO_STATISTICS, backend.fetch, login.company_name],
    queryFn: () => fetchInstallerInfoStatistics(backend.fetch),
    refetchInterval: 60000,
  });

  const handleFilterClick = (filters, sorting = null) => {
    const activeFilters = filters.filter(({ value }) => value).map(({ type, value }) => ({ id: type, value }));
    // leads to the sites overview with these filters applied
    trackButtonClicked(`landing_filter_${activeFilters.map(({ id, value }) => `${id}_${value}`).join('_') || 'none'}`, 'landing_page', {
      filters: activeFilters.map(({ id, value }) => `${id}=${value}`).join(','),
    });
    // Keep sites_overview's persisted mute-view toggle in sync, since it otherwise takes
    // priority over the includeMutes navigation state below and would mask this filter.
    sessionStorage.setItem("sites_overview_mute_view", includeMutes ? "normal" : "mutes");
    navigate(NAVIGATION_PATH.SITE_ROOT, {
      state: {
        filters: activeFilters.length ? activeFilters : null,
        sorting:
          sorting != null
            ? sorting.filter(({ value }) => value).map(({ type, value }) => ({ id: type, desc: value }))
            : null,
        includeMutes,
      },
    });
  };

  const criticals = ErrorData?.critical != null ? Number(ErrorData.critical) : null;
  const errors = ErrorData?.error != null ? Number(ErrorData.error) : null;
  const warnings = ErrorData?.warning != null ? Number(ErrorData.warning) : null;
  const healthy = ErrorData?.healthy != null ? Number(ErrorData.healthy) : null;
  const pending = ErrorData?.pending != null ? Number(ErrorData.pending) : null;

  const battery_volume = formatVolume(
    infoStatisticsData?.total_battery_power ? Number(infoStatisticsData.total_battery_power / 1000) : null,
  );
  const curtailable_volume = formatVolume(
    infoStatisticsData?.total_curtailable_power ? Number(infoStatisticsData.total_curtailable_power / 1000) : null,
  );
  const activeSites = infoStatisticsData?.active_sites ? Number(infoStatisticsData.active_sites) : null;
  const uncommissionedSites = infoStatisticsData?.uncommissioned_sites
    ? Number(infoStatisticsData.uncommissioned_sites)
    : null;

  return (
    <Container width="100%" height="100%" spacing={2} sx={{ marginBottom: 2, marginTop: 2 }} maxWidth="xl">
      <Grid container spacing={2} justifyContent="center">
        <Grid item md={4} xs={12} display="flex" flexDirection="column">
          <div
            style={{ cursor: "pointer" }}
            onClick={() => handleFilterClick([{ type: "commissioning_completed", value: "true" }])}
          >
            <InfoStat
              title="Active Sites"
              value={activeSites ? activeSites : "N/A"}
              unit={activeSites && uncommissionedSites ? "/" + (activeSites + uncommissionedSites) : null}
              isLoading={true}
            />
          </div>
          <HealthCard
            criticals={criticals}
            errors={errors}
            warnings={warnings}
            healthy={healthy}
            pending={pending}
            handleFilterClick={handleFilterClick}
            fetchingErrorData={fetchingErrorData}
            includeMutes={includeMutes}
            setIncludeMutes={setIncludeMutes}
          />

          <StatisticsCard title="Credit Line Per KW" metric="profit_per_kw" unit="€" sx={{ marginTop: 2 }} />
        </Grid>

        <Grid item md={4} xs={12} display="flex" flexDirection="column">
          {/*<div style={{cursor: "pointer"}}
                     onClick={() => handleFilterClick([{type: "commissioning_completed", value: "false"}])}>
                    <InfoStat tooltip={TOOLTIPS_EN.INACTIVE_SITES} title="Inactive Sites"
                              value={uncommissionedSites ? uncommissionedSites : "N/A"} isLoading={true}/>
                </div>*/}
          <InfoStat
            tooltip={TOOLTIPS_EN.INSTALLED_BATTERY_POWER}
            title="Installed Battery Power"
            value={battery_volume.value}
            unit={battery_volume.unit}
          />
          <CommissioningCard
            sx={{ mt: 2 }}
            handleFilterClick={handleFilterClick}
            commissioningData={commissioningData}
          />
          <UserActivityCard marginTop={2} />
        </Grid>
        <Grid item md={4} xs={12} display="flex" flexDirection="column">
          <InfoStat
            tooltip={TOOLTIPS_EN.CURTAILABLE_POWER}
            title="Curtailable Power"
            value={curtailable_volume.value}
            unit={curtailable_volume.unit}
          />
          <FavouriteSitesCard marginTop={2} />
          <SiteActivityCard sx={{ mt: 2 }} />
        </Grid>
      </Grid>
    </Container>
  );
}
